// ============================================================================
// GROUPING WRAPPER - HOOK
// ============================================================================

import { BaseEntity } from "@/components/shared/UniversalCRMTable/types/shared";
import { useUniversalEntityPageContext } from "../UniversalEntityPage/contexts/UniversalEntityPageContext";
import type { GroupingWrapperProps, GroupingWrapperData } from "./types";

/**
 * Hook to get GroupingWrapper data from context or props
 * Prefers context when available (inside UniversalEntityPage)
 * Falls back to props for backward compatibility
 */
export function useGroupingWrapperData<T extends BaseEntity>(
  props: GroupingWrapperProps<T>
): GroupingWrapperData<T> {
  // Try to use context if available
  let contextData: GroupingWrapperData<T> | null = null;
  try {
    const context = useUniversalEntityPageContext<T>();
    contextData = {
      data: context.dataState.processedData,
      config: context.pageConfig.config,
      searchTerm: context.filterState.searchTerm,
      setSearchTerm: context.filterState.setSearchTerm,
      filters: context.filterState.tableFilters,
      setFilters: context.filterState.setTableFilters,
      onExport: context.callbacks.handleGroupingExport,
      onEdit: context.handlers.handleEditItem,
      onView: context.handlers.handleViewItem,
      onOpenDetailModal: context.handlers.handleOpenDetailModal,
      // Pagination props for card view
      hasNextPage: context.dataState.hasNextPage,
      isFetchingNextPage: context.dataState.isFetchingNextPage,
      onLoadMore: context.dataState.loadMore,
      // Entity hooks for actions (cards and table rows)
      hooks: context.pageConfig.hooks,
    };
  } catch {
    // Not in context, will use props
  }

  // Return context data if available, otherwise props
  return contextData || props;
}
